<template>
  <div class="task-list">
    <h2>任务列表</h2>

    <!-- 控制面板 -->
    <div class="control-panel">
      <!-- 排序功能 -->
      <div class="sort-controls">
        <label for="sort-select">排序方式：</label>
        <select id="sort-select" v-model="sortCriteria">
          <option value="original">原数据</option>
          <option value="type">任务类型</option>
          <option value="beginTime">开始时间</option>
          <option value="endTime">结束时间</option>
          <option value="difDate">日期点</option>
          <option value="dayTime">时间点</option>
          <option value="cycle">任务周期</option>
          <option value="completed">是否完成</option>
          <option value="starRating">星级排序</option>
        </select>

        <button class="sort-order-btn" @click="toggleSortOrder">
          {{ sortOrder === 'asc' ? '↑' : '↓' }}
        </button>
      </div>

      <!-- 筛选功能 -->
      <div class="filter-controls">
        <label for="type-filter">任务类型：</label>
        <select id="type-filter" v-model="typeFilter">
          <option value="">全部类型</option>
          <option v-for="type in store.types" :key="type.id" :value="type.id">
            {{ type.name }}
          </option>
        </select>

        <label for="cycle-filter">任务周期：</label>
        <select id="cycle-filter" v-model="cycleFilter">
          <option value="">全部周期</option>
          <option v-for="cycle in store.cycles" :key="cycle.id" :value="cycle.id">
            {{ cycle.name }}
          </option>
        </select>
      </div>

      <!-- 显示选项 -->
      <div class="show-options">
        <label>
          <input type="checkbox" v-model="showCompleted" />
          显示已完成任务
        </label>
        <label>
          <input type="checkbox" v-model="showDeleted" />
          显示已删除任务
        </label>
      </div>
    </div>

    <!-- 任务列表 -->
    <div v-if="filteredAndSortedTasks.length === 0" class="empty-state">
      <p>暂无任务</p>
    </div>
    <div v-else class="task-items">
      <div v-for="task in filteredAndSortedTasks" :key="task.id" class="task-item" :class="{
        completed: task.completed,
        deleted: !task.is_active,
        [`type-${task.typeId}`]: task.typeId,
        [`cycle-${task.cycleId}`]: task.cycleId
      }">
        <!-- 任务标签 -->
        <div class="task-tags">
          <span class="type-tag" :class="`type-${task.typeId}`">
            {{ getFirstCharOfType(task.typeId) }}
          </span>
          <span class="cycle-tag" :class="`cycle-${task.cycleId}`">
            {{ getFirstCharOfCycle(task.cycleId) }}
          </span>
        </div>

        <div class="task-checkbox-wrapper" @click="toggleTask(task.id)"
          :class="{ 'processing': isTaskProcessing(task.id) }">
          <input type="checkbox" :checked="task.completed" class="task-checkbox" />
          <span class="checkmark"></span>
        </div>
        <div class="task-info">
          <span class="task-title">{{ task.title }}</span>
          <span class="task-description">{{ task.description }}</span>

          <!-- 新增字段显示 -->
          <div class="task-new-fields">
            <div class="task-progress">
              <span class="progress-text">进度: {{ task.current_count }}/{{ task.target_count === 0 ? '∞' :
                task.target_count }}</span>
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: calculateProgress(task) + '%' }"></div>
              </div>
            </div>
            <div class="task-worth">
              <span class="worth-text">价值: ¥{{ task.worth }}</span>
            </div>
            <div v-if="task.bind_event" class="task-event">
              <span class="event-text">绑定事件: {{ task.bind_event }}</span>
            </div>

            <!-- 星级显示 -->
            <div class="task-star-rating">
              <span class="star-rating-label">星级:</span>
              <div class="star-rating-display">
                <span v-for="star in 5" :key="star" class="star" :class="{ 'filled': star <= (task.star_rating || 0) }">
                  ★
                </span>
              </div>
            </div>
          </div>

          <div class="task-details">
            <span v-if="task.beginTime || task.endTime" class="time-range">
              {{ task.beginTime }} - {{ task.endTime }}
            </span>
            <span v-if="task.difDate" class="date-point">{{ task.difDate }}</span>
            <span v-if="task.dayTime" class="time-point">{{ task.dayTime }}</span>
          </div>
        </div>
        <div class="task-actions">
          <!-- 星级设置 -->
          <div class="star-rating-input">
            <span class="star-rating-label">设为:</span>
            <div class="star-rating-edit">
              <span v-for="star in 5" :key="star" class="star" :class="{ 'filled': star <= (task.star_rating || 0) }"
                @click="setTaskStarRating(task.id, star)">
                ★
              </span>
            </div>
          </div>
          <button class="edit-btn" @click="editTask(task)">编辑</button>
          <button class="delete-btn" @click="deleteTask(task.id)">
            {{ task.is_active ? '删除' : '恢复' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useTodoStore } from '../stores/todoStore.js'
import TodoService from '@/services/todoService.js'
import notificationService from '@/services/notificationService.js'

export default {
  name: 'TaskList',
  setup() {
    const store = useTodoStore()

    // 排序相关状态
    const sortCriteria = ref('original')
    const sortOrder = ref('asc')

    // 筛选相关状态
    const typeFilter = ref('')
    const cycleFilter = ref('')
    const showCompleted = ref(false)
    const showDeleted = ref(false)

    // 用于跟踪正在处理的任务，防止重复点击
    const processingTasks = ref(new Set())

    // 切换排序顺序
    const toggleSortOrder = () => {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
    }

    // 获取任务类型的首个字符
    const getFirstCharOfType = (typeId) => {
      const type = store.types.find(t => t.id === typeId)
      return type ? type.name.charAt(0) : ''
    }

    // 获取任务周期的首个字符
    const getFirstCharOfCycle = (cycleId) => {
      const cycle = store.cycles.find(c => c.id === cycleId)
      return cycle ? cycle.name.charAt(0) : ''
    }

    // 计算任务进度百分比
    const calculateProgress = (task) => {
      // 当目标为0时，如果当前进度大于0则显示100%，否则显示0%
      if (task.target_count === 0) {
        return task.current_count > 0 ? 100 : 0;
      }
      // 当当前进度超过目标时，显示100%
      if (task.current_count >= task.target_count) return 100;
      // 正常计算进度百分比
      return Math.round((task.current_count / task.target_count) * 100);
    }

    // 计算筛选后的任务
    const filteredTasks = computed(() => {
      // 使用store中的filteredTasks而不是直接筛选原始任务
      let tasks = store.filteredTasks || store.tasks

      // 根据是否显示已完成任务进行筛选
      if (!showCompleted.value) {
        tasks = tasks.filter(task => !task.completed)
      }

      // 根据是否显示已删除任务进行筛选
      if (!showDeleted.value) {
        // 不显示已删除任务时，只显示活跃任务(is_active为true的任务)
        tasks = tasks.filter(task => task.is_active)
      } else {
        // 显示已删除任务时，显示所有任务
        // 这里可以不做额外处理，或者根据需要添加特定逻辑
      }

      // 根据任务类型筛选
      if (typeFilter.value) {
        tasks = tasks.filter(task => task.typeId === typeFilter.value)
      }

      // 根据任务周期筛选
      if (cycleFilter.value) {
        tasks = tasks.filter(task => task.cycleId === cycleFilter.value)
      }

      return tasks
    })

    // 计算排序后的任务
    const sortedTasks = computed(() => {
      if (sortCriteria.value === 'original') {
        return filteredTasks.value
      }

      const tasks = [...filteredTasks.value]

      tasks.sort((a, b) => {
        let aValue, bValue

        switch (sortCriteria.value) {
          case 'type':
            aValue = getFirstCharOfType(a.typeId)
            bValue = getFirstCharOfType(b.typeId)
            break
          case 'beginTime':
            aValue = a.beginTime || ''
            bValue = b.beginTime || ''
            break
          case 'endTime':
            aValue = a.endTime || ''
            bValue = b.endTime || ''
            break
          case 'difDate':
            aValue = a.difDate || ''
            bValue = b.difDate || ''
            break
          case 'dayTime':
            aValue = a.dayTime || ''
            bValue = b.dayTime || ''
            break
          case 'cycle':
            aValue = getFirstCharOfCycle(a.cycleId)
            bValue = getFirstCharOfCycle(b.cycleId)
            break
          case 'completed':
            aValue = a.completed
            bValue = b.completed
            break
          case 'starRating':
            aValue = a.star_rating || 0
            bValue = b.star_rating || 0
            break
          default:
            return 0
        }

        if (aValue < bValue) {
          return sortOrder.value === 'asc' ? -1 : 1
        }
        if (aValue > bValue) {
          return sortOrder.value === 'asc' ? 1 : -1
        }
        return 0
      })

      return tasks
    })

    // 最终的筛选和排序后的任务
    const filteredAndSortedTasks = computed(() => {
      return sortedTasks.value
    })

    const toggleTask = async (taskId) => {
      console.log('toggleTask called with taskId:', taskId);

      // 检查任务是否正在处理中，防止重复点击
      if (processingTasks.value.has(taskId)) {
        console.log('Task is already being processed, skipping duplicate click:', taskId);
        return;
      }

      // 标记任务为正在处理状态
      processingTasks.value.add(taskId);

      try {
        // 找到要切换的任务
        const task = store.tasks.find(t => t.id === taskId);
        console.log('Found task:', task);
        if (task) {
          console.log('Task completed status before toggle:', task.completed);
          // 根据cycleid的值判断是否为计数计划
          console.log('Task cycleId:', task.cycleId);
          console.log('Store cycles:', store.cycles);
          console.log('Store cycles length:', store.cycles.length);
          store.cycles.forEach((cycle, index) => {
            console.log(`Cycle ${index}: id=${cycle.id}, name='${cycle.name}', nameLength=${cycle.name.length}`);
          });
          const cycle = store.cycles.find(c => c.id === task.cycleId);
          console.log('Found cycle:', cycle);
          if (cycle) {
            console.log('Cycle name:', cycle.name);
            console.log('Cycle name length:', cycle.name.length);
            console.log('Cycle name char codes:', cycle.name.split('').map(c => c.charCodeAt(0)));
          }
          // 修复计数计划判断逻辑，确保正确识别计数计划任务
          const isCountingPlan = cycle && cycle.name.trim() === '计数计划';
          console.log('isCountingPlan:', isCountingPlan);
          if (cycle) {
            console.log('Comparing cycle name with 计数计划:');
            console.log('  Cycle name:', cycle.name);
            console.log('  Expected name: 计数计划');
            console.log('  Names equal:', cycle.name === '计数计划');
            console.log('  Trimmed names equal:', cycle.name.trim() === '计数计划');
            console.log('  Cycle name char codes:', [...cycle.name].map(c => c.charCodeAt(0)));
            console.log('  Expected name char codes:', [...'计数计划'].map(c => c.charCodeAt(0)));
          }

          // 对于计数计划任务，我们不需要改变completed状态，但需要增加current_count
          // 对于普通任务，我们需要切换completed状态
          const finalCompleted = isCountingPlan ? task.completed : !task.completed;

          // 计算新的当前计数值
          // 对于计数计划任务，每次点击都应该增加计数
          // 对于普通任务，只有在完成时才增加计数
          const newCurrentCount = isCountingPlan ? task.current_count + 1 :
            (finalCompleted && !task.completed ? task.current_count + 1 :
              (!finalCompleted && task.completed ? task.current_count - 1 : task.current_count));

          // 调用API更新任务完成状态
          const data = {
            todo_id: taskId,
            is_completed: finalCompleted,
            current_count: newCurrentCount
          }
          console.log('Sending data to updateTodoItem:', data);

          const response = await TodoService.updateTodoItem(data)
          console.log('Received response from updateTodoItem:', response);
          if (response.status === 'ok') {
            // 更新store中的任务状态
            const updatedTasks = store.tasks.map(t =>
              t.id === taskId ? {
                ...t,
                completed: response.todo_item.is_completed,
                title: response.todo_item.title,
                description: response.todo_item.category,
                typeId: response.todo_item.typeid,
                cycleId: response.todo_item.cycleid,
                beginTime: response.todo_item.begin_time,
                endTime: response.todo_item.end_time,
                difDate: response.todo_item.dif_date,
                dayTime: response.todo_item.day_time,
                createdAt: response.todo_item.created_at,
                updatedAt: response.todo_item.updated_at,
                is_active: response.todo_item.is_active,
                current_count: response.todo_item.current_count,
                target_count: response.todo_item.target_count,
                worth: response.todo_item.worth,
                bind_event: response.todo_item.bind_event
              } : t
            )
            store.updateTasks(updatedTasks)
            console.log('任务状态更新成功')

            // 显示成功通知
            notificationService.success(response.message || '任务状态更新成功', '成功', 3000)

            // 对于计数计划任务，更新用户价值（创建WorthHistory记录）
            console.log('Checking if task is counting plan for worth history creation:', isCountingPlan);
            if (isCountingPlan) {
              console.log('Creating WorthHistory record for counting plan task');
              try {
                // 直接更新用户价值，这会创建WorthHistory记录
                const worthData = {
                  todo_id: taskId,
                  is_completed: true, // 对于计数任务，我们总是认为是完成状态
                  worth_change: task.worth || 0 // 即使价值为0也要记录
                };

                const worthResponse = await TodoService.updateWorth(worthData);
                if (worthResponse.status === 'ok') {
                  console.log('计数计划任务的WorthHistory记录创建成功:', worthResponse.message);
                  // 更新用户总价值
                  try {
                    const userWorthResponse = await TodoService.getUserWorth();
                    if (userWorthResponse.status === 'ok') {
                      store.updateUserWorth(userWorthResponse.current_worth);
                    }
                  } catch (error) {
                    console.error('获取用户价值失败:', error);
                  }
                } else {
                  console.error('Failed to create WorthHistory record:', worthResponse.message);
                }
              } catch (worthError) {
                console.error('创建WorthHistory记录失败:', worthError);
              }
            } else {
              console.log('Not a counting plan task, skipping WorthHistory creation');
            }

            // 如果任务有价值，且是普通任务完成了（或者计数任务），更新用户价值
            if (task.worth > 0) {
              // 对于计数计划任务，每次点击都增加价值
              // 对于普通任务，只有在完成时才增加价值
              const shouldUpdateWorth = isCountingPlan ? true :
                (finalCompleted && !task.completed); // 从未完成变为完成

              if (shouldUpdateWorth) {
                try {
                  const worthData = {
                    todo_id: taskId,
                    is_completed: true, // 对于价值更新，我们总是认为是"完成"状态
                    worth_change: task.worth
                  }

                  const worthResponse = await TodoService.updateWorth(worthData)
                  if (worthResponse.status === 'ok') {
                    console.log('价值更新成功:', worthResponse.message)
                    // 更新用户总价值
                    try {
                      const userWorthResponse = await TodoService.getUserWorth();
                      if (userWorthResponse.status === 'ok') {
                        store.updateUserWorth(userWorthResponse.current_worth);
                      }
                    } catch (error) {
                      console.error('获取用户价值失败:', error);
                    }
                  } else {
                    console.error('价值更新失败:', worthResponse.message)
                  }
                } catch (worthError) {
                  console.error('价值更新异常:', worthError)
                }
              }
            }
          } else {
            console.error('任务状态更新失败:', response.message)
            // 显示错误通知
            notificationService.error(response.message || '任务状态更新失败', '错误', 3000)
          }
        } else {
          console.error('Task not found for id:', taskId);
        }
      } catch (error) {
        console.error('切换任务状态失败:', error)
      } finally {
        // 无论成功或失败，都要从处理状态中移除任务
        processingTasks.value.delete(taskId);
      }
    }
    const editTask = (task) => {
      console.log('编辑任务:', task)
      // 打开编辑模态框
      store.openEditTaskModal(task)
    }

    // 设置任务星级
    const setTaskStarRating = async (taskId, rating) => {
      try {
        // 找到要更新星级的任务
        const task = store.tasks.find(t => t.id === taskId)
        if (!task) return;

        // 如果点击的是当前星级，则取消星级（设为0）
        const newRating = task.star_rating === rating ? 0 : rating;

        // 调用API更新任务星级
        const data = {
          todo_id: taskId,
          star_rating: newRating
        }

        const response = await TodoService.updateTaskStarRating(data)
        if (response.status === 'ok') {
          // 更新store中的任务星级
          const updatedTasks = store.tasks.map(t =>
            t.id === taskId ? {
              ...t,
              star_rating: response.todo_item.star_rating,
              title: response.todo_item.title,
              description: response.todo_item.category,
              completed: response.todo_item.is_completed,
              typeId: response.todo_item.typeid,
              cycleId: response.todo_item.cycleid,
              beginTime: response.todo_item.begin_time,
              endTime: response.todo_item.end_time,
              difDate: response.todo_item.dif_date,
              dayTime: response.todo_item.day_time,
              createdAt: response.todo_item.created_at,
              updatedAt: response.todo_item.updated_at,
              is_active: response.todo_item.is_active,
              current_count: response.todo_item.current_count,
              target_count: response.todo_item.target_count,
              worth: response.todo_item.worth,
              bind_event: response.todo_item.bind_event
            } : t
          )
          store.updateTasks(updatedTasks)
          console.log('任务星级更新成功')
          // 显示成功通知
          notificationService.success(response.message || '任务星级更新成功', '成功', 3000)
        } else {
          console.error('任务星级更新失败:', response.message)
          // 显示错误通知
          notificationService.error(response.message || '任务星级更新失败', '错误', 3000)
        }
      } catch (error) {
        console.error('设置任务星级异常:', error)
      }
    }

    const deleteTask = async (taskId) => {
      try {
        // 找到要操作的任务
        const task = store.tasks.find(t => t.id === taskId)
        if (!task) return;

        if (task.is_active) {
          // 删除任务（实际上是设置为非激活状态）
          if (confirm('确定要删除这个任务吗？')) {
            const data = {
              todo_id: taskId,
              is_active: false
            }

            const response = await TodoService.updateTodoItem(data)
            if (response.status === 'ok') {
              // 更新store中的任务状态
              const updatedTasks = store.tasks.map(t =>
                t.id === taskId ? {
                  ...t,
                  is_active: response.todo_item.is_active,
                  title: response.todo_item.title,
                  description: response.todo_item.category,
                  completed: response.todo_item.is_completed,
                  typeId: response.todo_item.typeid,
                  cycleId: response.todo_item.cycleid,
                  beginTime: response.todo_item.begin_time,
                  endTime: response.todo_item.end_time,
                  difDate: response.todo_item.dif_date,
                  dayTime: response.todo_item.day_time,
                  createdAt: response.todo_item.created_at,
                  updatedAt: response.todo_item.updated_at,
                  current_count: response.todo_item.current_count,
                  target_count: response.todo_item.target_count,
                  worth: response.todo_item.worth,
                  bind_event: response.todo_item.bind_event
                } : t
              )
              store.updateTasks(updatedTasks)
              console.log('任务删除成功')
              // 显示成功通知
              notificationService.success(response.message || '任务删除成功', '成功', 3000)
            } else {
              console.error('任务删除失败:', response.message)
              // 显示错误通知
              notificationService.error(response.message || '任务删除失败', '错误', 3000)
            }
          }
        } else {
          // 恢复任务
          if (confirm('确定要恢复这个任务吗？')) {
            const data = {
              todo_id: taskId,
              is_active: true
            }

            const response = await TodoService.updateTodoItem(data)
            if (response.status === 'ok') {
              // 更新store中的任务状态
              const updatedTasks = store.tasks.map(t =>
                t.id === taskId ? {
                  ...t,
                  is_active: response.todo_item.is_active,
                  title: response.todo_item.title,
                  description: response.todo_item.category,
                  completed: response.todo_item.is_completed,
                  typeId: response.todo_item.typeid,
                  cycleId: response.todo_item.cycleid,
                  beginTime: response.todo_item.begin_time,
                  endTime: response.todo_item.end_time,
                  difDate: response.todo_item.dif_date,
                  dayTime: response.todo_item.day_time,
                  createdAt: response.todo_item.created_at,
                  updatedAt: response.todo_item.updated_at,
                  current_count: response.todo_item.current_count,
                  target_count: response.todo_item.target_count,
                  worth: response.todo_item.worth,
                  bind_event: response.todo_item.bind_event
                } : t
              )
              store.updateTasks(updatedTasks)
              console.log('任务恢复成功')
              // 显示成功通知
              notificationService.success(response.message || '任务恢复成功', '成功', 3000)
            } else {
              console.error('任务恢复失败:', response.message)
              // 显示错误通知
              notificationService.error(response.message || '任务恢复失败', '错误', 3000)
            }
          }
        }
      } catch (error) {
        console.error('操作任务失败:', error)
      }
    }

    // 检查任务是否正在处理中
    const isTaskProcessing = (taskId) => {
      return processingTasks.value.has(taskId);
    }


    return {
      store,
      sortCriteria,
      sortOrder,
      typeFilter,
      cycleFilter,
      showCompleted,
      showDeleted,
      filteredAndSortedTasks,
      getFirstCharOfType,
      getFirstCharOfCycle,
      calculateProgress,
      toggleSortOrder,
      toggleTask,
      editTask,
      deleteTask,
      setTaskStarRating,
      isTaskProcessing
    }
  }
}
</script>

<style scoped>
.task-list {
  padding: 20px;
}

.control-panel {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: center;
}

.sort-controls,
.filter-controls,
.show-options {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  min-width: 150px;
}

.sort-controls label,
.filter-controls label {
  font-weight: 500;
  color: #333;
}

.sort-controls select,
.filter-controls select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.sort-order-btn {
  background: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.3s ease;
}

.sort-order-btn:hover {
  background: #359c6d;
}

.show-options input {
  margin-right: 5px;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #7f8c8d;
}

.task-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.task-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 15px;
  border: 2px solid #ddd;
  border-radius: 8px;
  background: white;
  transition: all 0.3s ease;
  position: relative;
}

/* 任务标签 */
.task-tags {
  position: absolute;
  top: -10px;
  left: 10px;
  display: flex;
  gap: 5px;
}

.type-tag,
.cycle-tag {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.type-tag {
  background: #ff6b6b;
}

.cycle-tag {
  background: #4ecdc4;
}

/* 根据任务类型设置不同颜色 */
.task-item.type-1 .type-tag {
  background: #ff6b6b;
}

.task-item.type-2 .type-tag {
  background: #4ecdc4;
}

.task-item.type-3 .type-tag {
  background: #45b7d1;
}

.task-item.type-4 .type-tag {
  background: #96ceb4;
}

.task-item.type-5 .type-tag {
  background: #feca57;
}

/* 根据任务周期设置不同颜色 */
.task-item.cycle-1 .cycle-tag {
  background: #ff9ff3;
}

.task-item.cycle-2 .cycle-tag {
  background: #54a0ff;
}

.task-item.cycle-3 .cycle-tag {
  background: #5f27cd;
}

.task-item.cycle-4 .cycle-tag {
  background: #00d2d3;
}

.task-item.cycle-5 .cycle-tag {
  background: #ff9f43;
}

.task-item.cycle-6 .cycle-tag {
  background: #10ac84;
}

/* 根据任务类型设置不同颜色 */
.task-item.type-1 {
  border-left: 4px solid #ff6b6b;
}

.task-item.type-2 {
  border-left: 4px solid #4ecdc4;
}

.task-item.type-3 {
  border-left: 4px solid #45b7d1;
}

.task-item.type-4 {
  border-left: 4px solid #96ceb4;
}

.task-item.type-5 {
  border-left: 4px solid #feca57;
}

/* 根据任务周期设置不同边框样式 */
.task-item.cycle-1 {
  border-style: solid;
}

.task-item.cycle-2 {
  border-style: dashed;
}

.task-item.cycle-3 {
  border-style: dotted;
}

.task-item.cycle-4 {
  border-style: double;
}

.task-item.cycle-5 {
  border-style: groove;
}

.task-item.cycle-6 {
  border-style: ridge;
}

/* 已完成和已删除任务的样式 */
.task-item.completed {
  background: #f8f9fa;
  opacity: 0.7;
}

.task-item.completed .task-title {
  text-decoration: line-through;
}

.task-item.deleted {
  background: #ffebee;
  opacity: 0.7;
  border-color: #ffcdd2;
}

.task-item.deleted .task-title {
  text-decoration: line-through;
  color: #9e9e9e;
}

.task-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  /* 允许flex项目收缩 */
}

.task-title {
  font-weight: 500;
  margin-bottom: 5px;
}

.task-description {
  color: #7f8c8d;
  font-size: 14px;
  margin-bottom: 5px;
}

/* 新增字段样式 */
.task-new-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 5px 0;
}

.task-progress,
.task-worth,
.task-event {
  font-size: 12px;
}

.progress-text,
.worth-text,
.event-text {
  display: block;
  margin-bottom: 3px;
}

.progress-bar {
  width: 100px;
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  min-width: 80px;
}

.progress-fill {
  height: 100%;
  background-color: #42b983;
  transition: width 0.3s ease;
}

.task-details {
  display: flex;
  gap: 10px;
  font-size: 12px;
}

.time-range,
.date-point,
.time-point {
  background: #e0e0e0;
  padding: 2px 6px;
  border-radius: 4px;
  color: #666;
}

.task-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 10px;
  justify-content: flex-end;
}

.edit-btn,
.delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background 0.3s ease;
  font-size: 14px;
}

.edit-btn {
  color: #42b983;
}

.edit-btn:hover {
  background: #e8f4f1;
}

.delete-btn {
  color: #ff6b6b;
  font-size: 14px;
  width: auto;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 5px 10px;
}

.delete-btn:hover {
  background: #ffebee;
}

/* 自定义复选框样式 */
.task-checkbox-wrapper {
  position: relative;
  display: inline-block;
  width: 24px;
  height: 24px;
  margin-right: 15px;
  cursor: pointer;
}

.task-checkbox {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 24px;
  width: 24px;
  background-color: #fff;
  border: 2px solid #ddd;
  border-radius: 4px;
  transition: all 0.3s ease;
  pointer-events: none;
  /* 防止checkmark干扰点击事件 */
}

/* 鼠标悬停效果 */
.task-checkbox-wrapper:hover .checkmark {
  border-color: #42b983;
}

/* 选中状态 */
.task-checkbox:checked+.checkmark {
  background-color: #42b983;
  border-color: #42b983;
}

/* 选中状态的对勾符号 */
.task-checkbox-wrapper .checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.task-checkbox:checked+.checkmark:after {
  display: block;
}

/* 星级显示样式 */
.task-star-rating {
  display: flex;
  align-items: center;
  margin-top: 10px;
}

.star-rating-label {
  margin-right: 10px;
  font-size: 14px;
  color: #666;
}

.star-rating-display,
.star-rating-edit {
  display: flex;
}

.star {
  color: #ddd;
  font-size: 16px;
  margin-right: 2px;
  transition: color 0.3s ease;
  cursor: pointer;
}

.star.filled {
  color: #ffd700;
}

.star-rating-input {
  display: flex;
  align-items: center;
  margin-right: 10px;
}

/* 对勾符号的样式 */
.task-checkbox-wrapper .checkmark:after {
  left: 8px;
  top: 4px;
  width: 6px;
  height: 12px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* 正在处理中的任务复选框样式 */
.task-checkbox-wrapper.processing {
  cursor: not-allowed;
  opacity: 0.6;
}

.task-checkbox-wrapper.processing .checkmark {
  cursor: not-allowed;
  opacity: 0.6;
  border-color: #aaa;
}

.task-checkbox-wrapper.processing:hover .checkmark {
  border-color: #aaa;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .control-panel {
    flex-direction: column;
    align-items: stretch;
  }

  .sort-controls,
  .filter-controls,
  .show-options {
    width: 100%;
    align-items: stretch;
  }

  .sort-controls label,
  .filter-controls label {
    margin-bottom: 5px;
  }

  .sort-controls select,
  .filter-controls select {
    width: 100%;
  }

  .task-item {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
  }

  .task-checkbox-wrapper {
    position: relative;
    margin-bottom: 10px;
  }

  .task-tags {
    position: relative;
    top: 0;
    left: 0;
    margin-bottom: 10px;
  }

  .task-info {
    width: 100%;
    margin-bottom: 10px;
  }

  .task-actions {
    width: 100%;
    justify-content: space-between;
  }

  .task-new-fields {
    flex-direction: column;
    width: 100%;
  }

  .task-details {
    flex-direction: column;
    gap: 5px;
    width: 100%;
  }

  .edit-btn,
  .delete-btn {
    flex: 1;
    min-width: 45%;
  }
}

/* 针对非常小的屏幕进行优化 */
@media (max-width: 480px) {
  .task-item {
    padding: 10px;
  }

  .task-actions {
    flex-direction: column;
    gap: 8px;
  }

  .edit-btn,
  .delete-btn {
    min-width: 100%;
    width: 100%;
  }

  .control-panel {
    padding: 10px;
  }

  .sort-controls select,
  .filter-controls select {
    font-size: 14px;
    /* 防止iOS缩放 */
  }
}
</style>